/**
 * Writing a module record, and the one edit another screen makes to one.
 *
 * A module is assembled rather than composed: a creator names the subject and the
 * level, and the record is written *then* — because the whole flow after that
 * point depends on the module having an address. Capsules are written inside it
 * (which needs the module's own uri to append to), and its slots have to survive
 * the creator leaving for a studio and coming back. So the record exists as a
 * draft from the second step, and this is what writes it: one function that
 * creates or updates, plus the read-modify-write the capsule studio uses when it
 * hands a freshly written capsule back to the module that spawned it.
 *
 * Not here: the post. Publishing a module is two writes and the post is the one
 * that can fail in the middle, so it belongs with the publisher that knows the
 * order (see `#/components/modules/PublishModuleDialog`), not here where it would
 * be one more thing this file's callers could do out of sequence.
 */
import {type AtpAgent, AtUri} from '@atproto/api'

import {MODULE_COLLECTION, type ModuleItem} from '#/lib/modulePlaylist'
import {appendModuleItem} from '#/lib/moduleItems'
import {moduleViewOf, type ModuleRecordView} from '#/lib/moduleRecord'

/** The record a module is written as, from the studio's own fields. */
export function moduleRecordOf({
  title,
  description,
  status,
  items,
  level,
  subject,
  postUri,
  postCid,
  visibility,
  createdAt,
  updatedAt,
}: {
  title: string
  description: string
  status: 'draft' | 'published'
  items: readonly ModuleItem[]
  level?: string | null
  subject?: string | null
  postUri?: string
  postCid?: string
  visibility?: string
  /** Kept across edits so a module is not backdated every time it is saved. */
  createdAt: string
  updatedAt?: string
}): Record<string, unknown> {
  return {
    $type: MODULE_COLLECTION,
    title,
    description,
    status,
    /*
     * Both addresses per place, and only the ones that exist.
     *
     * The capsule record is what the gate and the coin total read; the post is
     * what the carousel draws. A draft capsule has no post, so the item carries
     * none — which is the same fact the gate refuses on, said in the data rather
     * than derived from it a second time.
     */
    items: items.map(item => ({
      uri: item.uri,
      ...(item.cid ? {cid: item.cid} : null),
      ...(item.postUri ? {postUri: item.postUri} : null),
    })),
    ...(level ? {level} : null),
    ...(subject ? {subject} : null),
    ...(postUri ? {post: {uri: postUri, cid: postCid ?? ''}} : null),
    ...(visibility ? {visibility} : null),
    createdAt,
    ...(updatedAt ? {updatedAt} : null),
  }
}

/** Reads a module record by its address, in the shape every reader uses. */
export async function readModuleRecord({
  agent,
  moduleUri,
}: {
  agent: AtpAgent
  moduleUri: string
}): Promise<ModuleRecordView> {
  const atUri = new AtUri(moduleUri)
  const res = await agent.com.atproto.repo.getRecord({
    repo: atUri.hostname,
    collection: atUri.collection,
    rkey: atUri.rkey,
  })
  return moduleViewOf(res.data.uri, res.data.cid ?? '', res.data.value)
}

/**
 * Writes a module record, creating it the first time and updating it after.
 *
 * Which write to use is decided by whether the caller already has an address,
 * rather than by asking the repo: the studio knows, because it is the thing that
 * created the record, and a `listRecords` to find out would be a request that can
 * only ever agree with the answer the caller already has.
 */
export async function writeModuleRecord({
  agent,
  did,
  moduleUri,
  record,
}: {
  agent: AtpAgent
  did: string
  /** The record's address when it already exists; absent for the first write. */
  moduleUri?: string
  record: Record<string, unknown>
}): Promise<{uri: string; cid: string}> {
  if (moduleUri) {
    const rkey = new AtUri(moduleUri).rkey
    const res = await agent.com.atproto.repo.putRecord({
      repo: did,
      collection: MODULE_COLLECTION,
      rkey,
      record,
    })
    return {uri: res.data.uri, cid: res.data.cid ?? ''}
  }
  const res = await agent.com.atproto.repo.createRecord({
    repo: did,
    collection: MODULE_COLLECTION,
    record,
  })
  return {uri: res.data.uri, cid: res.data.cid ?? ''}
}

/**
 * Adds a capsule to a module's playlist, on the module's own record.
 *
 * This is how a capsule written *inside* a module finds its way in. The capsule
 * studio has just published (or drafted) a capsule and knows the module it was
 * opened from; the alternative — handing it back through the navigation stack —
 * would only work on the one device that did the writing, and a module is a
 * record whose whole point is that it is assembled across sessions.
 *
 * Read, append, write. The read is the price of not clobbering a playlist that
 * another device edited in the meantime, and the append is idempotent (see
 * `appendModuleItem`), so a double tap cannot put the same capsule in twice.
 */
export async function appendCapsuleToModule({
  agent,
  did,
  moduleUri,
  item,
}: {
  agent: AtpAgent
  did: string
  moduleUri: string
  item: ModuleItem
}): Promise<{uri: string; cid: string; items: ModuleItem[]}> {
  const current = await readModuleRecord({agent, moduleUri})
  const items = appendModuleItem(current.items, item)
  const now = new Date().toISOString()
  const {uri, cid} = await writeModuleRecord({
    agent,
    did,
    moduleUri,
    record: moduleRecordOf({
      title: current.title,
      description: current.description,
      status: current.status,
      items,
      level: current.level,
      subject: current.subject,
      postUri: current.postUri,
      postCid: current.postCid,
      visibility: current.visibility,
      createdAt: current.createdAt,
      updatedAt: now,
    }),
  })
  return {uri, cid, items}
}
